import { AnimatedContentLoader } from '@root/components/AnimatedContentLoader'
import { TextTooltip } from '@root/components/InfoPopup'
import { useCurrentUser } from '@root/query'
import { useDocument } from '@root/query/hooks/documents'
import { DocumentListItem } from '@root/pages/DocumentPage/DocumentListItem'
import {
  AuditTrailChangeReasons,
  AuditTrailItemType,
  IAuditTrailComment,
  IAuditTrailObjectBase,
  IAuditTrailWorkflowOrigin,
  IAuditTrailWrapper,
  IChangeDocument,
  IAuditUser
} from '@root/query/auditTrail'
import theme from '@root/styles/theme'
import { formatDate, formatRelativeTime } from '@root/utils'
import dayjs from 'dayjs'
import { ReactElement, ReactNode } from 'react'
import ReactMarkdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import { FaQuestionCircle } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import UserDetails from './UserDetails'
import { Link } from 'react-router-dom'
import { getDocumentsPageUrl, getWorkflowBuilderPageUrl } from '@root/constants'
import {
  PermissionSet,
  useBusinessContext,
  useFlag,
  useFlagsLoaded,
  usePermission
} from '@root/context'
import type { Pluggable } from 'unified'

interface IHeaderBlock {
  auditRecord: IAuditTrailWrapper
  seenAt: string | null
}

const formatDateWithPastReference = (date: Date | dayjs.Dayjs | string | undefined): string => {
  if (!date) {
    return ''
  }

  const day = dayjs(date)
  const isRecent = day.isAfter(dayjs().subtract(2, 'day'), 'day')

  return isRecent ? formatRelativeTime(day) : formatDate(date)
}

const DocumentTooltipContent: React.FC<{ id: number }> = ({ id }) => {
  const [t] = useTranslation()
  const { data, isLoading, isError } = useDocument({ documentId: id })
  const isMissingDocument = !isLoading && !isError && !data

  return (
    <AnimatedContentLoader
      isLoading={isLoading}
      isError={isError}
      errorDescription={t('uas.documents.noData')}
      isEmpty={isMissingDocument}
      isEmptyDescription={t('uas.documents.noData')}
    >
      {data && <DocumentListItem document={data} isSelected={false} isLastOfType isFirstOfType />}
    </AnimatedContentLoader>
  )
}

interface MarkdownDocumentProps extends Record<string, unknown> {
  id?: string | number
  children: ReactNode
}

interface WorkflowTagProps extends MarkdownDocumentProps {
  'data-name'?: string
}

const DocumentNode: React.FC<MarkdownDocumentProps> = ({ children, id }) => {
  const { slug } = useBusinessContext()
  const documentId = Number(id)

  if (!Number.isFinite(documentId) || documentId <= 0) {
    return <>{children}</>
  }

  return (
    <TextTooltip renderTooltip={() => <DocumentTooltipContent id={documentId} />}>
      <Link to={getDocumentsPageUrl(slug, { id: documentId })}>{children}</Link>
    </TextTooltip>
  )
}

const WorkflowNode: React.FC<WorkflowTagProps> = ({ children, id, 'data-name': workflowName }) => {
  const { slug } = useBusinessContext()
  const isAdmin = usePermission(PermissionSet.ADMIN)
  const flagsLoaded = useFlagsLoaded()
  const workflowsEnabled = useFlag('workflows').enabled
  const workflowId = Number(id)
  const canOpen =
    Number.isFinite(workflowId) && workflowId > 0 && flagsLoaded && workflowsEnabled && isAdmin
  const word = canOpen ? (
    <WorkflowLink to={getWorkflowBuilderPageUrl(slug, workflowId)}>{children}</WorkflowLink>
  ) : (
    <>{children}</>
  )

  if (!workflowName) {
    return word
  }

  return (
    <TextTooltip tooltip={workflowName} placement="bottom-start">
      {word}
    </TextTooltip>
  )
}

const rawHtmlPlugin = rehypeRaw as unknown as Pluggable

const renderMarkdownMessage = (
  message: string,
  components?: Record<string, (props: any) => ReactElement>
) => (
  <ReactMarkdown
    linkTarget="_blank"
    rehypePlugins={[rawHtmlPlugin]}
    components={{
      p: props => <div {...props} />,
      ...components
    }}
  >
    {message}
  </ReactMarkdown>
)

const renderAuditTrailMessage = ({
  message,
  user,
  workflow,
  defaultChangeReason
}: {
  message: string
  user?: IAuditUser
  workflow?: IAuditTrailWorkflowOrigin | null
  defaultChangeReason?: AuditTrailChangeReasons
}) =>
  renderMarkdownMessage(message, {
    ...(user && {
      user: props => (
        <UserDetails
          user={user}
          workflow={workflow}
          changeReason={
            (props['data-change-reason'] as AuditTrailChangeReasons) ?? defaultChangeReason
          }
        />
      )
    }),
    document: props => <DocumentNode {...props} />,
    workflow: props => <WorkflowNode {...props} />
  })

export const HeaderBlock: React.FC<IHeaderBlock> = ({ auditRecord, seenAt }) => {
  const isAuditAction = auditRecord.type === AuditTrailItemType.ACTIVITY
  const isAuditComment = auditRecord.type === AuditTrailItemType.COMMENT
  const { data: currentUser } = useCurrentUser()
  let actionItem: IAuditTrailObjectBase<IChangeDocument> | undefined
  let commentItem: IAuditTrailComment | undefined

  if (isAuditAction) {
    actionItem = auditRecord.item
  } else if (isAuditComment) {
    commentItem = auditRecord.item
  }

  const auditDate = actionItem?.change_time ?? commentItem?.created_at
  const auditUser = actionItem?.changed_by ?? commentItem?.created_by

  const isNew =
    Boolean(auditDate) &&
    dayjs(auditDate).isAfter(seenAt) &&
    Boolean(auditUser?.id) &&
    auditUser.id !== currentUser?.id

  const translatedBlock =
    (isAuditAction || isAuditComment) &&
    renderAuditTrailMessage({
      message: auditRecord.message,
      user: auditUser,
      workflow: commentItem?.created_by_workflow,
      defaultChangeReason: actionItem?.change_reason
    })

  return (
    <StepTitle>
      {translatedBlock}
      <StepTitleDate>
        {formatDateWithPastReference(auditDate)}
        {isNew && <NewTag />}
      </StepTitleDate>
    </StepTitle>
  )
}

interface IDiscontinuationHeaderBlock {
  date: string
  message: string
}

export const DiscontinuationHeaderBlock: React.FC<IDiscontinuationHeaderBlock> = ({
  date,
  message
}) => {
  const [t] = useTranslation()

  return (
    <StepTitle style={{ alignItems: 'center' }}>
      {renderMarkdownMessage(message)}
      <TextTooltip
        tooltip={t('components.auditTrail.discontinuationInfo', { historyBegin: formatDate(date) })}
      >
        <FaQuestionCircle
          style={{
            color: theme.colors.metalGray,
            marginLeft: `${theme.spacing.xxs}rem`,
            marginBottom: `${theme.spacing.xxxs}rem`
          }}
        />
      </TextTooltip>

      <StepTitleDate>{formatDateWithPastReference(date)}</StepTitleDate>
    </StepTitle>
  )
}

const WorkflowLink = styled(Link)`
  color: ${({ theme }) => theme.colors.primary};
`

const StepTitle = styled.div`
  display: flex;
  flex: 1;
  width: auto;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  margin-bottom: 0;
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  line-height: var(--audit-step-title-line-height);
  align-items: center;

  & > svg {
    width: ${({ theme }) => theme.spacing.sm}rem;
    min-width: ${({ theme }) => theme.spacing.sm}rem;
    height: ${({ theme }) => theme.spacing.sm}rem;
    min-height: ${({ theme }) => theme.spacing.sm}rem;
    align-self: center;
    margin-left: ${({ theme }) => theme.spacing.xxs}rem;
  }
`

const StepTitleDate = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  align-items: center;
  text-align: right;
  margin-left: auto;
`

const NewTag: React.FC = () => {
  const [t] = useTranslation()
  return <NewTagWrapper>{t('components.auditTrail.new')}</NewTagWrapper>
}

const NewTagWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  text-transform: uppercase;

  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  color: ${({ theme }) => theme.colors.neutralWhite};

  background: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSize.xxs}rem;
  font-weight: bold;
  letter-spacing: 0.04em;

  /* Uppercase text reserves the font's descender space, so equal vertical padding
     reads bottom-heavy. The extra top pixel centers the capitals optically. */
  padding: 2px ${({ theme }) => theme.spacing.xs}rem 1px;
  line-height: 1.3;
`
